import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, BadgeCheck } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
  { label: "Les aides", href: "#aides" },
  { label: "Simulateur", href: "#simulateur" },
  { label: "Méthode", href: "#methode" },
  { label: "Zones", href: "#zones" },
  { label: "FAQ", href: "#faq" },
  { label: "Climatisation", href: "/climatisation", highlight: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    if (!href.startsWith("#")) {
      setOpen(false);
      return;
    }
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-forest-deep/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-[72px] sm:px-8">
        <a
          href="#"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            scrollToTop();
          }}
          className="flex items-center gap-2.5"
        >
          <img
            src="/logo-enersolutions.png"
            alt="Ener Solutions"
            className="h-10 w-auto rounded-md bg-white/95 px-2 py-1.5"
          />
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => go(e, l.href)}
              className={`text-sm font-medium transition-colors ${
                l.highlight
                  ? "rounded-full border border-sky-400/40 px-3 py-1 text-sky-300 hover:bg-sky-400/10"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-4 lg:flex">
          <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">
            <BadgeCheck className="h-3.5 w-3.5" /> RGE QualiPAC
          </span>
          <a
            href="tel:+33535759803"
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-semibold text-white/85 transition-colors hover:text-emerald-300"
          >
            <Phone className="h-4 w-4" /> 05 35 75 98 03
          </a>
          <button
            data-testid="nav-cta"
            onClick={(e) => go(e, "#simulateur")}
            className="rounded-full bg-emerald-400 px-5 py-2.5 text-sm font-bold text-forest-deep transition-all hover:scale-[1.03] hover:bg-emerald-300"
          >
            Tester mon éligibilité
          </button>
        </div>

        <button
          data-testid="nav-menu-button"
          className="grid h-10 w-10 place-items-center rounded-lg border border-white/15 text-white lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-white/10 bg-forest-deep/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={(e) => go(e, l.href)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+33535759803"
                data-testid="nav-mobile-phone"
                className="mt-2 flex items-center gap-2 rounded-lg px-3 py-3 text-base font-semibold text-emerald-300"
              >
                <Phone className="h-4 w-4" /> 05 35 75 98 03
              </a>
              <button
                data-testid="nav-mobile-cta"
                onClick={(e) => go(e, "#simulateur")}
                className="mt-1 rounded-full bg-emerald-400 px-5 py-3 text-sm font-bold text-forest-deep"
              >
                Tester mon éligibilité
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
